import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Menu,
  Sun,
  Moon,
  Columns2,
  LayoutDashboard,
  Eye,
  Settings,
  LogOut,
  CreditCard,
  ChevronDown,
  Layers,
  Users,
  Info,
  Play,
  Sparkles,
  SlidersHorizontal,
  ArrowLeftRight,
  FileText,
  Download,
  ShieldCheck,
  Server,
  Coins,
  Zap,
  LifeBuoy,
  Activity,
  Store,
  LayoutGrid,
} from 'lucide-react'
import { NotificationBell } from '@twasta/page-runtime/shell'
import { platformNotificationRuntime } from '@/api/support'
import { useAuth } from '@/auth/AuthContext'
import { useProjectStore } from '@/store/projectStore'
import { useThemeStore } from '@/store/themeStore'
import { useJourneyStore } from '@/store/journeyStore'
import { useCreditsStore } from '@/store/creditsStore'
import { useVisiblePolling } from '@/hooks/useVisiblePolling'
import { formatCredits } from '@/api/billing'
import type { WorkbenchTab } from '@/store/projectStore'
import AboutModal from '@/components/common/AboutModal'
import AppIcon from '@/components/common/AppIcon'
import FrsModal from '@/components/common/FrsModal'
import ProjectSettingsModal from '@/components/userMemory/ProjectSettingsModal'
import InstanceAdminModal from '@/pages/admin/InstanceAdminModal'
import UserMemoryGate from '@/components/userMemory/UserMemoryGate'

// The Workbench's tabs are NOT a constant: they come from the open project's
// App type journey (store/journeyStore ← backend data/journeys/*.json), with
// Advanced always at the right.

/** Routes where workbench tabs should be visible (project-context pages) */
const PROJECT_CONTEXT_PREFIXES = [
  '/workbench',
  '/generateapp/finetune',
  '/generateapp/preview',
  '/designers/',
  '/srceditor/',
  '/deploy/',
]

/** Routes (incl. /builder/:project) where the Builder|Workbench mode pill should show. */
function isBuilderRoute(pathname: string): boolean {
  return pathname.startsWith('/builder/')
}
function isAnyProjectModeRoute(pathname: string): boolean {
  return (
    PROJECT_CONTEXT_PREFIXES.some((p) => pathname.startsWith(p))
    || isBuilderRoute(pathname)
  )
}

/** Determine which tab should be highlighted based on the current route */
function getActiveTabForRoute(pathname: string): WorkbenchTab | null {
  if (pathname.startsWith('/workbench')) return null // use store's activeTab
  // The specification editor is Advanced › Specification editor.
  if (pathname.startsWith('/generateapp/finetune')) return 'advanced'
  if (pathname.startsWith('/generateapp/preview')) return 'next'
  // Database, Code and Deploy pages are reached via the Advanced landing tab.
  if (pathname.startsWith('/designers/')) return 'advanced'
  if (pathname.startsWith('/srceditor/')) return 'advanced'
  if (pathname.startsWith('/deploy/')) return 'advanced'
  return null
}

function tabStyle(isActive: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.75rem',
    flexShrink: 0,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? '#ffffff' : 'var(--color-text-secondary)',
    background: isActive ? 'var(--color-accent)' : 'transparent',
    transition: 'all var(--transition-fast)',
    whiteSpace: 'nowrap',
    border: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
  }
}

interface TopBarProps {
  onToggleSidebar: () => void
}

export default function TopBar({ onToggleSidebar }: TopBarProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { currentProject, activeTab, setActiveTab, prototypeLeaveGuard } = useProjectStore()
  const { theme, toggleTheme } = useThemeStore()
  const journey = useJourneyStore((s) => s.journey)
  const journeyProject = useJourneyStore((s) => s.project)
  const refreshJourneyFor = useJourneyStore((s) => s.refresh)
  const folderForJourney = currentProject?.application.folder_name
  // The tab set belongs to the open project's journey — load it wherever the
  // project's top bar shows (designers and tools too, not just /workbench).
  useEffect(() => {
    if (folderForJourney && journeyProject !== folderForJourney) void refreshJourneyFor(folderForJourney)
  }, [folderForJourney, journeyProject, refreshJourneyFor])
  const tabsForProject = journey && journeyProject === folderForJourney ? journey.tabs : []
  const needsYou = !!journey && (journey.next.stage === 'question' || journey.next.stage === 'review')
  const [menuOpen, setMenuOpen] = useState(false)
  const [aboutOpen, setAboutOpen] = useState(false)
  const [instanceAdminOpen, setInstanceAdminOpen] = useState(false)
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false)
  const [frsOpen, setFrsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)

  // Track the header's own width (not the viewport) — the sidebar can be wide
  // or maximized, so a viewport media query isn't enough.
  const [headerWidth, setHeaderWidth] = useState(0)
  useEffect(() => {
    const el = headerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setHeaderWidth(e.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const isProjectContext = PROJECT_CONTEXT_PREFIXES.some((p) => location.pathname.startsWith(p))
  const isBuilderMode = isBuilderRoute(location.pathname)
  const routeTab = getActiveTabForRoute(location.pathname)
  const effectiveTab = routeTab ?? activeTab
  // Mode switch is only meaningful when a project is loaded and we're in
  // one of the two project-scoped surfaces.
  const canSwitchModes = !!currentProject
    && isAnyProjectModeRoute(location.pathname)
    && currentProject.application.title !== 'Invalid project information'

  // Folded chrome (avatar-only user button, icon-only Open app) only on a
  // genuinely narrow header. The project header no longer folds just because
  // the tabs ran out of room — the tab row moves to its own line instead, so
  // the app name and Open app never shrink when a tab is picked.
  const compactChrome = headerWidth > 0 && headerWidth < 720

  // Name · tabs · actions on one row when they fit. When the tabs need more
  // room than is left beside the name and actions, they take their OWN row
  // under them — the name (the hero) and the actions never move or shrink.
  const infoRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const [navOwnRow, setNavOwnRow] = useState(false)
  useLayoutEffect(() => {
    const nav = navRef.current
    if (!nav || !headerWidth) { if (navOwnRow) setNavOwnRow(false); return }
    // Content width of the (nowrap) tab row, whatever its container is now.
    const needed = Array.from(nav.children).reduce((w, c) => w + (c as HTMLElement).offsetWidth + 4, 0)
    const used = (infoRef.current?.offsetWidth || 0) + (actionsRef.current?.offsetWidth || 0) + 36 + 64
    const own = needed > headerWidth - used
    if (own !== navOwnRow) setNavOwnRow(own)
  })

  // ── Live credit balance ───────────────────────────────────────────────
  // Three refresh paths, because a stale number here is the one the user
  // notices: a 60s poll while the tab is visible, an immediate refresh when
  // the dropdown opens, and `refreshCredits()` from the agent hooks when a
  // turn finishes (the moment credits actually move).
  const credits = useCreditsStore()
  const refreshBalance = credits.refresh
  // At/below the wallet's low-balance threshold (or empty) — the user menu
  // shows the balance in the warning colour so it is noticed before a refusal.
  const creditsLow = credits.enabled && credits.balance != null && (
    credits.balance <= 0
    || (credits.lowThreshold != null && credits.balance <= credits.lowThreshold))
  useVisiblePolling(refreshBalance, 60000)
  useEffect(() => {
    if (menuOpen) void refreshBalance()
  }, [menuOpen, refreshBalance])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])

  const handleTabClick = (tab: WorkbenchTab) => {
    const doNav = () => {
      if (tab === 'deploy' && currentProject) {
        navigate(`/deploy/${encodeURIComponent(currentProject.application.folder_name)}`)
        return
      }
      setActiveTab(tab)
      // Navigate to the canonical deep-linkable URL so browser back/forward
      // walks through tab switches. Falls back to bare /workbench when no
      // project is loaded (WorkbenchPage canonicalizes once one is).
      const target = currentProject
        ? `/workbench/${encodeURIComponent(currentProject.application.folder_name)}/${tab}`
        : '/workbench'
      if (location.pathname !== target) {
        navigate(target)
      }
    }
    // Leaving the objects tab (Screens / Pages…) with unsaved designer or
    // menu changes routes through its discard/save prompt (registered in the
    // store) before navigating.
    if (tab !== activeTab && prototypeLeaveGuard) {
      prototypeLeaveGuard(doNav)
      return
    }
    doNav()
  }

  const handleLogout = async () => {
    setMenuOpen(false)
    await logout()
    navigate('/')
  }

  const getUserInitials = () => {
    if (!user) return '?'
    const name = user.user_name || user.email
    return name
      .split(/[\s@]+/)
      .map((w: string) => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  return (
    <>
    <header
      ref={headerRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: navOwnRow ? 'wrap' : 'nowrap',
        minHeight: 52,
        padding: '0.375rem 1.25rem',
        background: isProjectContext && currentProject ? 'var(--color-bg-secondary)' : 'var(--color-bg)',
        borderBottom: isProjectContext && currentProject ? '1px solid var(--color-border)' : undefined,
        flexShrink: 0,
        gap: '0.5rem 1rem',
        position: 'relative',
        zIndex: 'var(--z-dropdown)',
      }}
    >
      {/* Hamburger toggle */}
      <button
        onClick={onToggleSidebar}
        title="Toggle sidebar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text-secondary)',
          transition: 'all var(--transition-fast)',
          flexShrink: 0,
        }}
      >
        <Menu size={20} />
      </button>

      {/* Current project info */}
      {currentProject && (isProjectContext || isBuilderMode) && (
        <div
          ref={infoRef}
          className="topbar-project-info"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: 600,
            fontSize: '1rem',
            color: 'var(--color-text)',
            flex: '0 1 auto',
            minWidth: 0,
          }}
        >
          <AppIcon
            icon={currentProject.application.icon}
            imageData={currentProject.application.icon_image_data}
            size={isProjectContext ? 34 : 24}
            iconSize={isProjectContext ? 20 : 16}
            color="#fff"
            background={currentProject.application.theme_color || 'var(--color-accent)'}
            borderRadius={isProjectContext ? '8px' : 'var(--radius-sm)'}
          />
          {isProjectContext ? (
            // The app name is the hero: always shown in full. The status line
            // under it takes the name's width and truncates instead.
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
                {currentProject.application.title}
              </span>
              {journey && journeyProject === folderForJourney && (
                <span style={{ width: 0, minWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary)' }}
                  title={journey.summary}>
                  {journey.summary}
                </span>
              )}
            </span>
          ) : (
            <span className="truncate" style={{ maxWidth: 360 }}>
              {currentProject.application.title}
            </span>
          )}
        </div>
      )}

      {/* Non-project page title */}
      {!isProjectContext && !isBuilderMode && !currentProject && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--color-text-secondary)',
            fontSize: '0.9375rem',
          }}
        >
          <Layers size={18} />
          <span>Twasta.ai</span>
        </div>
      )}

      {/* Tab navigation (on any project-context page with a selected project, hidden for invalid projects) */}
      {isProjectContext && currentProject && currentProject.application.title !== 'Invalid project information' && (
        <div
          ref={navRef}
          className="topbar-tabs"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            // ONE row: name · tabs · actions never change places. If the row
            // is too narrow for every tab, the tabs scroll sideways instead of
            // stacking or pushing the actions onto another line.
            flex: navOwnRow ? '1 0 100%' : '1 1 0',
            order: navOwnRow ? 3 : undefined,
            scrollbarWidth: 'thin',
            minWidth: 0,
            flexWrap: 'nowrap',
            overflowX: 'auto',
          }}
        >
          {tabsForProject.map((tab) => {
            const isActive = effectiveTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                aria-current={isActive ? 'page' : undefined}
                data-tw-tab={tab.id}
                // Active = SOLID accent pill with white text (the workbench
                // header's primary affordance); idle = secondary text, hover surface-2.
                style={tabStyle(isActive)}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--color-surface-2)' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
              >
                {tab.label}
                {tab.view === 'next' && needsYou && (
                  <span aria-label="needs your action" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--color-error)', display: 'inline-block' }} />
                )}
              </button>
            )
          })}
          <span style={{ flex: '1 1 16px' }} />
          {(() => {
            const isActive = effectiveTab === 'advanced'
            return (
              <button
                type="button"
                onClick={() => handleTabClick('advanced')}
                aria-current={isActive ? 'page' : undefined}
                data-tw-tab="advanced"
                style={tabStyle(isActive)}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--color-surface-2)' }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
              >
                <Settings size={14} style={{ flexShrink: 0 }} />
                Advanced
              </button>
            )
          })()}
        </div>
      )}

      {/* Spacer when no tabs (workbench tabs only render on workbench routes) */}
      {!(isProjectContext && currentProject) && <div style={{ flex: 1 }} />}
      {isBuilderMode && <div style={{ flex: 1 }} />}

      {/* Right section */}
      <div
        ref={actionsRef}
        style={{
          order: navOwnRow ? 2 : undefined,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginLeft: 'auto',
          flexShrink: 0,
        }}
      >
        {/* Spec button — Builder Mode only. Opens the Functional Spec (FRS)
            in a read-only modal so the user can see what the AI understood
            without leaving the live preview + chat. */}
        {isBuilderMode && currentProject
          && currentProject.application.title !== 'Invalid project information' && (
          <button
            onClick={() => setFrsOpen(true)}
            title="View the functional specification"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0 0.75rem',
              height: 36,
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text-secondary)',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
            }}
          >
            <FileText size={14} />
            <span className="topbar-spec-label">Spec</span>
          </button>
        )}

        {/* Sites + Run are scoped to the project workspaces — the workbench
            (`/workbench`), a builder project (`/builder/:project`) and the
            project-scoped tools the Advanced tab opens (designers, code
            editor, deploy). Opening one of those from Advanced must not make
            Run disappear, so they resolve their project from the URL. On the
            new-application flows (describe / finetune / preview), the samples
            gallery, settings, etc. there's no project to run, so we hide
            them. `/builder` alone (the hub) doesn't qualify — only a project
            under it does. */}
        {(() => {
          const path = location.pathname
          const inWorkbench = path === '/workbench' || path.startsWith('/workbench/')
          const inBuilder = /^\/builder\/[^/]+/.test(path)
          // `/designers/<kind>/<project>/...`, `/srceditor/<project>`,
          // `/deploy/<project>/...`, `/j2ee-deploy/<project>` — every one of
          // these carries the project folder as a path segment.
          // `style-placement` is keyed by a master id, not a project.
          const fromUrl =
            /^\/designers\/(?!style-placement\/)[^/]+\/([^/]+)/.exec(path)
            || /^\/(?:srceditor|deploy|j2ee-deploy)\/([^/]+)/.exec(path)
          const urlFolder = fromUrl ? decodeURIComponent(fromUrl[1]) : null
          if (!inWorkbench && !inBuilder && !urlFolder) return null
          // Prefer the folder in the address bar: on a designer/tool route the
          // store may still be catching up (or restoring after a refresh), and
          // running the previously-selected project would be plain wrong.
          const runFolder = urlFolder || currentProject?.application.folder_name || null
          // The "has it been generated yet?" gate can only be applied when the
          // loaded project *is* the one in the URL. When it isn't loaded yet,
          // the route itself is proof enough that the project exists.
          const gateProject =
            currentProject
            && (!urlFolder || currentProject.application.folder_name === urlFolder)
              ? currentProject
              : null
          const canRun =
            !!runFolder
            && (gateProject
              ? gateProject.application.generated || (gateProject.navigation?.length ?? 0) > 0
              : !!urlFolder)
          return (
            <>
              {/* Run button — opens the application in a new tab as an end
                  user sees it. The site(s) are reachable from inside the Run
                  view itself. Only shown once the app has actually been
                  generated (i.e. has nav items or the saved `generated` flag
                  is set). In the draft / generating phases there's nothing
                  to run, so the button stays hidden. */}
              {canRun && runFolder && (
          <button
            onClick={() => {
              const folder = runFolder
              // Tenant-scoped run URL so a shared link is explicit about which
              // organization's project it points at. `_` when the user has no
              // tenant (resolution stays session-scoped either way).
              const tenant = user?.tenant_id || '_'
              window.open(
                `/run/${encodeURIComponent(tenant)}/${encodeURIComponent(folder)}`,
                '_blank',
                'noopener',
              )
            }}
            title="Open the app as its users see it (new tab)"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: compactChrome ? 0 : '0 0.75rem',
              width: compactChrome ? 36 : undefined,
              height: 40,
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              background: 'var(--color-secondary-btn)',
              border: '1px solid var(--color-secondary-btn)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all var(--transition-fast)',
            }}
          >
            <Play size={14} />
            {!compactChrome && 'Open app'}
          </button>
        )}
            </>
          )
        })()}

        {/* What happened that concerns this builder — a plan to approve, a
            build that failed, a release out. The SAME bell the Run view
            mounts, over the platform's own notification store. */}
        {user && (
          <NotificationBell
            runtime={platformNotificationRuntime}
            onOpenLink={(link) => {
              // Support links open on the project's Support tab — an issue
              // at the issue (`?issue=`), a release on Releases (`?tab=`).
              if (!link?.project) return
              const folder = encodeURIComponent(String(link.project))
              if (link.kind === 'support_issue' || link.open === 'support_issue') {
                const n = Number(link.number)
                navigate(`/workbench/${folder}/advanced/support${n ? `?issue=${n}` : ''}`)
              } else if (link.kind === 'support_release') {
                navigate(`/workbench/${folder}/advanced/support?tab=releases`)
              }
            }}
          />
        )}

        {/* User menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.25rem 0.5rem',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              fontSize: '0.875rem',
              transition: 'all var(--transition-fast)',
              background: 'transparent',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-full)',
                background: 'linear-gradient(135deg, #0061D0, #009974)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '0.8125rem',
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </div>
            {!compactChrome && (
              <>
                <span
                  className="topbar-username"
                  style={{
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {user?.user_name || user?.email || ''}
                </span>
                <ChevronDown size={14} className="topbar-chevron" style={{ color: 'var(--color-text-secondary)' }} />
              </>
            )}
          </button>

          {/* Dropdown menu */}
          {menuOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                right: 0,
                minWidth: 200,
                background: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                padding: '0.25rem',
                zIndex: 'var(--z-dropdown)',
                animation: 'slideDown var(--transition-fast) ease-out',
              }}
            >
              {/* User info */}
              <div
                style={{
                  padding: '0.625rem 0.75rem',
                  borderBottom: '1px solid var(--color-border)',
                  marginBottom: '0.25rem',
                }}
              >
                <div
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-text)',
                    marginBottom: '0.125rem',
                  }}
                >
                  {user?.user_name || 'User'}
                </div>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {user?.email}
                </div>
              </div>

              {/* Credits — the live balance, with the plan as a chip. Clicking
                  goes to Billing, which is also where a top-up starts. */}
              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/billing')
                }}
                title="View your credit balance, plan and invoices"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <Coins size={16} style={{ color: creditsLow ? 'var(--color-warning)' : 'var(--color-accent)' }} />
                <span style={{ flex: 1 }}>
                  {credits.loaded ? (
                    <>
                      <strong style={creditsLow ? { color: 'var(--color-warning)' } : undefined}>
                        {formatCredits(credits.balance)}
                      </strong>
                      <span style={{ color: 'var(--color-text-secondary)' }}>
                        {creditsLow ? (credits.balance != null && credits.balance <= 0 ? ' credits · top up' : ' credits · low') : ' credits'}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: 'var(--color-text-secondary)' }}>Credits</span>
                  )}
                </span>
                {credits.planLabel && (
                  <span
                    style={{
                      fontSize: '0.8125rem',
                      padding: '0.0625rem 0.375rem',
                      borderRadius: 999,
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {credits.planLabel}
                  </span>
                )}
              </button>
              {/* Agent turns left today — only when the plan caps them. The
                  headline is the allowance the user is building on now (AI
                  credits vs their own key / a free model); the line under it
                  shows the other one and when both reset. */}
              {credits.loaded && credits.agentTurns?.capped && (() => {
                const t = credits.agentTurns
                const cur = t[t.current]
                const otherKind = t.current === 'credit' ? 'own' : 'credit'
                const other = t[otherKind]
                const curLabel = t.current === 'credit' ? 'on AI credits' : 'on your own AI'
                const otherLabel = otherKind === 'credit' ? 'on AI credits' : 'on own key / free model'
                const resets = new Date(t.resets_at)
                const resetsAt = Number.isNaN(resets.getTime())
                  ? 'midnight UTC'
                  : resets.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                const low = cur.remaining != null && cur.limit != null
                  && cur.remaining <= Math.max(1, Math.ceil(cur.limit * 0.15))
                return (
                  <button
                    onClick={() => {
                      setMenuOpen(false)
                      navigate('/payments/plans')
                    }}
                    title="Agent turns your plan includes each day — upgrade for unlimited"
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.5rem', width: '100%',
                      padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text)', fontSize: '0.875rem', textAlign: 'left',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                    }}
                  >
                    <Zap size={16} style={{ marginTop: 1, color: low ? 'var(--color-warning)' : 'var(--color-accent)' }} />
                    <span style={{ flex: 1 }}>
                      {cur.remaining == null ? (
                        <span>Unlimited turns {curLabel}</span>
                      ) : (
                        <>
                          <strong style={low ? { color: 'var(--color-warning)' } : undefined}>
                            {cur.remaining} of {cur.limit}
                          </strong>
                          <span style={{ color: 'var(--color-text-secondary)' }}> turns left today {curLabel}</span>
                        </>
                      )}
                      <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {other.limit == null
                          ? `Unlimited ${otherLabel}`
                          : `${other.remaining} of ${other.limit} ${otherLabel}`}
                        {` · resets ${resetsAt}`}
                      </span>
                    </span>
                  </button>
                )
              })()}
              <div style={{ height: 1, background: 'var(--color-border)', margin: '0.25rem 0' }} />

              {/* Mode switch — only shown when a project is loaded */}
              {canSwitchModes && (
                <>
                  <button
                    onClick={() => {
                      if (!currentProject) return
                      setMenuOpen(false)
                      if (isBuilderMode) {
                        navigate('/workbench')
                      } else {
                        navigate(`/builder/${encodeURIComponent(currentProject.application.folder_name)}`)
                      }
                    }}
                    title={
                      isBuilderMode
                        ? 'Open this project in the full technical Workbench'
                        : 'Open this project in Builder Mode (chat-driven)'
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--color-text)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textAlign: 'left',
                      transition: 'all var(--transition-fast)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {isBuilderMode ? (
                      <SlidersHorizontal size={16} style={{ color: 'var(--color-text-secondary)' }} />
                    ) : (
                      <Sparkles size={16} style={{ color: 'var(--color-accent)' }} />
                    )}
                    <span style={{ flex: 1 }}>
                      {isBuilderMode ? 'Switch to Workbench' : 'Switch to Builder Mode'}
                    </span>
                    <ArrowLeftRight size={12} style={{ color: 'var(--color-text-secondary)', opacity: 0.6 }} />
                  </button>
                  <div
                    style={{
                      height: 1,
                      background: 'var(--color-border)',
                      margin: '0.25rem 0',
                    }}
                  />
                </>
              )}

              {/* Menu items */}
              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/enterprise/users')
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <Users size={16} />
                {user?.organization ? `Team · ${user.organization}` : 'Team'}
              </button>

              {user?.role === 'ENTERPRISE_ADMIN' && (
                <button
                  onClick={() => {
                    setMenuOpen(false)
                    navigate('/enterprise/gateways')
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <Server size={16} />
                  Database Gateways
                </button>
              )}

              {user?.role === 'ENTERPRISE_ADMIN' && (
                <button
                  onClick={() => {
                    setMenuOpen(false)
                    navigate('/enterprise/activity')
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <Activity size={16} />
                  Organization activity
                </button>
              )}

              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/marketplace')
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <Store size={16} />
                Marketplace
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/marketplace/my-apps')
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <LayoutGrid size={16} />
                My apps
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/settings')
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <Settings size={16} />
                Settings
              </button>

              {currentProject && (
                <button
                  onClick={() => {
                    setMenuOpen(false)
                    setProjectSettingsOpen(true)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text-secondary)',
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <SlidersHorizontal size={16} />
                  Project settings
                </button>
              )}

              {user?.is_instance_admin && (
                <button
                  onClick={() => {
                    setMenuOpen(false)
                    setInstanceAdminOpen(true)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-accent)',
                    fontSize: '0.875rem',
                    textAlign: 'left',
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  <ShieldCheck size={16} />
                  Instance Admin
                </button>
              )}

              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/runtime-package')
                }}
                title="Bundle one or more apps + the runtime server into a single download you can run on your own machine"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <Download size={16} />
                <span style={{ flex: 1 }}>Run locally</span>
                {credits.loaded && !credits.allowDownload && (
                  <span
                    style={{
                      fontSize: '0.8125rem',
                      padding: '0.0625rem 0.375rem',
                      borderRadius: 999,
                      background: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {credits.downloadPlanLabel || 'Enterprise'}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/billing')
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <CreditCard size={16} />
                Billing &amp; Plans
              </button>

              <button
                onClick={() => {
                  setMenuOpen(false)
                  setAboutOpen(true)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <Info size={16} />
                About
              </button>

              <button
                onClick={toggleTheme}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text-secondary)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                {theme === 'dark' ? 'Light theme' : 'Dark theme'}
              </button>

              <div
                style={{
                  height: 1,
                  background: 'var(--color-border)',
                  margin: '0.25rem 0',
                }}
              />

              <button
                onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-error)',
                  fontSize: '0.875rem',
                  textAlign: 'left',
                  transition: 'all var(--transition-fast)',
                }}
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>

    {aboutOpen && <AboutModal onClose={() => setAboutOpen(false)} />}
    {instanceAdminOpen && <InstanceAdminModal onClose={() => setInstanceAdminOpen(false)} />}
    {projectSettingsOpen && currentProject && (
      <ProjectSettingsModal
        projectId={currentProject.application.folder_name}
        surface="internal"
        onClose={() => setProjectSettingsOpen(false)}
      />
    )}
    {/* Ask the builder for any pending prompt-variable values once per session
        when a project is open (same prompt the end-user sees at runtime). */}
    {currentProject && (
      <UserMemoryGate projectId={currentProject.application.folder_name} surface="internal" />
    )}
    {currentProject && (
      <FrsModal
        open={frsOpen}
        project={currentProject.application.folder_name}
        onClose={() => setFrsOpen(false)}
      />
    )}
  </>
  )
}
